// Unread texts for the nav badge (owner request 2026-10-03, migration 0024): GET /api/sms/unread every 20 s while
// signed in. When the count rises, a toast says who texted ("New text from Peach Plumbing"). The Texts page and the
// call screen's Text panel refresh it after opening a conversation (which marks it read) or sending.
import { useEffect, useState } from 'preact/hooks';
import type { SmsUnread } from '../../src/shared/api-sms';
import { toast } from './components/ui';
import { smsApi } from './dialer/sms';

const POLL_MS = 20_000;

let current: SmsUnread | null = null;
const subs = new Set<(s: SmsUnread | null) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inflight: Promise<SmsUnread | null> | null = null;

export function refreshSmsUnread(): Promise<SmsUnread | null> {
  if (inflight) return inflight;
  inflight = smsApi.unread()
    .then((next) => {
      const prev = current;
      current = next;
      // Only a rise after the first answer: signing in to five old unread texts is not five new ones.
      if (prev && next.count > prev.count && next.latest && next.latest.messageId !== prev.latest?.messageId) {
        toast(`New text from ${next.latest.from}`, 'info');
      }
      for (const f of subs) f(next);
      return next;
    })
    .catch(() => current)
    .finally(() => { inflight = null; });
  return inflight;
}

/** Sign-out: forget the count and stop polling. */
export function resetSmsUnread(): void {
  current = null;
  if (timer) { clearInterval(timer); timer = null; }
  for (const f of subs) f(null);
}

export function useSmsUnread(): SmsUnread | null {
  const [s, setS] = useState<SmsUnread | null>(current);
  useEffect(() => {
    subs.add(setS);
    if (!current) void refreshSmsUnread();
    if (!timer) timer = setInterval(() => { if (subs.size) void refreshSmsUnread(); }, POLL_MS);
    return () => { subs.delete(setS); };
  }, []);
  return s;
}
